<template>
  <main class="app-content">
    <div class="app-title">
      <div>
        <h1>
          <font-awesome-icon icon="bullhorn"/>
          Release Notes
        </h1>
      </div>
    </div>
    <div class="row">
      <div class="col-12">
        <b-tabs v-model="filterFormModel.statusTab" @input="onTabChange">
          <b-tab title="Tất cả"></b-tab>
          <b-tab title="Draft"></b-tab>
          <b-tab title="Published"></b-tab>
        </b-tabs>
        <div class="tile">
          <div class="form-inline">
            <b-form-input class="mr-1 mb-1" v-model="filterFormModel.keyword" v-on:keydown.enter="load"
                          placeholder="Tìm theo version, nội dung..."></b-form-input>
            <button @click="load" class="btn btn-primary mr-1 mb-1">{{ $t('search') }}</button>
            <button @click="reset" class="btn btn-secondary mr-1 mb-1">Reset</button>
          </div>
        </div>
      </div>

      <div class="col-12">
        <div class="c-sorting">
          <div class="row">
            <div class="col-lg-12 c-sorting__right">
              <div class="c-sorting__btn">
                <button @click="create" class="btn btn-sm btn-success mr-1">
                  <font-awesome-icon icon="plus"/>&nbsp;Tạo mới
                </button>
              </div>
            </div>
          </div>
        </div>
        <div class="c-paging clearfix">
          <div class="c-paging__count" v-if="itemCount">
            {{ $t('found', [itemCount]) }}
          </div>
          <div class="c-paging__right">
            <b-pagination v-if="itemCount > filterFormModel.pageSize" class="pull-right" size="sm"
                          v-model="filterFormModel.page" :total-rows="itemCount"
                          :per-page="filterFormModel.pageSize" @input="load"></b-pagination>
          </div>
        </div>
      </div>

      <div class="col-12" v-if="items.length">
        <div class="c-smart-table tile">
          <div class="row c-smart-table__title">
            <div class="col-lg-2">Version</div>
            <div class="col-lg-1">Platform</div>
            <div class="col-lg-4">Nội dung</div>
            <div class="col-lg-1">Trạng thái</div>
            <div class="col-lg-2">Thời gian</div>
            <div class="col-lg-2"></div>
          </div>
          <div class="row" v-for="item in items" :key="item._id">
            <div class="col-lg-2">
              <p><strong>v{{ item.version }}</strong></p>
            </div>
            <div class="col-lg-1">
              <p>
                <span class="badge" :class="platformClass(item.platform)">{{ item.platform }}</span>
              </p>
            </div>
            <div class="col-lg-4">
              <p class="text-truncate" style="max-width: 400px">{{ stripMarkdown(item.content) }}</p>
            </div>
            <div class="col-lg-1">
              <p>
                <span v-if="item.publishTime > 0" class="badge badge-success">Published</span>
                <span v-else class="badge badge-warning">Draft</span>
              </p>
            </div>
            <div class="col-lg-2">
              <p v-if="item.publishTime > 0">{{ formatTime(item.publishTime) }}</p>
              <p v-else class="text-muted">{{ formatTime(item.createTime) }}</p>
            </div>
            <div class="col-lg-2">
              <div class="c-smart-table__content">
                <div class="c-buttons clearfix">
                  <div class="c-buttons__col">
                    <button @click="preview(item)" type="button" class="btn btn-sm btn-outline-primary mr-1 mb-1">
                      <font-awesome-icon icon="eye"/>
                    </button>
                    <button @click="update(item._id)" type="button" class="btn btn-sm btn-info mr-1 mb-1">
                      <font-awesome-icon icon="edit"/>
                    </button>
                    <button v-if="item.publishTime === 0" @click="showPublishModal(item)"
                            type="button" class="btn btn-sm btn-success mr-1 mb-1">
                      <font-awesome-icon icon="check"/>
                      Publish
                    </button>
                    <button v-if="item.publishTime > 0" @click="unpublish(item._id)"
                            type="button" class="btn btn-sm btn-warning mr-1 mb-1">
                      Gỡ
                    </button>
                    <button @click="del(item._id)" type="button" class="btn btn-sm btn-danger mr-1 mb-1">
                      <font-awesome-icon icon="trash"/>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-12 text-center text-muted" v-if="!items.length && !listLoading">
        Chưa có release note nào
      </div>
    </div>

    <!-- Modal Tạo/Sửa -->
    <b-modal v-model="showForm" :title="formTitle" size="lg" @ok="save" no-close-on-backdrop no-enforce-focus>
      <form-input label="Version" :model="formModel" attribute="version" :errors="formErrors"
                  placeholder="VD: 2.15.0"></form-input>
      <div class="form-group">
        <label>Platform</label>
        <b-form-select v-model="formModel.platform" :options="platformOptions"></b-form-select>
      </div>
      <div class="form-group">
        <label>Nội dung (Markdown)</label>
        <b-form-textarea v-model="formModel.content" rows="12" placeholder="Viết nội dung release note bằng markdown..."></b-form-textarea>
        <small class="text-muted">Hỗ trợ Markdown: **bold**, - list item, ### heading</small>
      </div>
      <div v-if="formModel.content" class="mt-3">
        <label>Xem trước:</label>
        <div class="border rounded p-3 bg-light" v-html="renderMarkdown(formModel.content)"></div>
      </div>
    </b-modal>

    <!-- Modal Preview -->
    <b-modal v-model="showPreview" title="Xem trước Release Note" size="lg" hide-footer scrollable>
      <div v-if="previewItem">
        <h4>v{{ previewItem.version }}
          <span class="badge ml-1" :class="platformClass(previewItem.platform)">{{ previewItem.platform }}</span>
        </h4>
        <p class="text-muted" v-if="previewItem.publishTime > 0">Đã publish: {{ formatTime(previewItem.publishTime) }}</p>
        <p class="text-muted" v-else>Draft — tạo lúc: {{ formatTime(previewItem.createTime) }}</p>
        <p v-if="previewItem.commits && previewItem.commits.length" class="text-muted">
          <small>{{ previewItem.commits.length }} commits</small>
        </p>
        <hr>
        <div v-html="renderMarkdown(previewItem.content)"></div>
      </div>
    </b-modal>

    <!-- Modal Publish -->
    <b-modal v-model="showPublish" title="Publish Release Note" @ok="doPublish" no-close-on-backdrop>
      <p>Bạn sắp publish <strong>v{{ publishItem ? publishItem.version : '' }}</strong></p>
      <div class="form-group">
        <b-form-checkbox v-model="publishOptions.notify">Gửi thông báo cho người dùng</b-form-checkbox>
      </div>
      <div v-if="publishOptions.notify" class="form-group ml-4">
        <b-form-radio-group v-model="publishOptions.notifyTarget" :options="notifyTargetOptions"></b-form-radio-group>
      </div>
    </b-modal>
  </main>
</template>

<script>
import List from "@/mixins/List";
import CRUD from "@/mixins/CRUD";
import moment from 'moment';

export default {
  mixins: [List, CRUD],
  data: function () {
    return {
      service: '/release-note',
      autoLoad: true,
      defaultFilterFormModel: {
        statusTab: 0,
        status: null,
        keyword: null,
      },
      defaultFormModel: {
        version: null,
        platform: 'all',
        content: '',
      },
      platformOptions: [
        {value: 'all', text: 'Tất cả'},
        {value: 'web', text: 'Web'},
        {value: 'mobile', text: 'Mobile'},
      ],
      notifyTargetOptions: [
        {value: 'all', text: 'Tất cả người dùng'},
        {value: 'owner', text: 'Chỉ chủ tài khoản (Owner)'},
      ],
      showPreview: false,
      previewItem: null,
      showPublish: false,
      publishItem: null,
      publishOptions: {
        notify: false,
        notifyTarget: 'all',
      },
    }
  },
  methods: {
    onTabChange() {
      let tab = this.filterFormModel.statusTab;
      this.filterFormModel.status = tab === 0 ? null : (tab === 1 ? 'draft' : 'published');
      this.load();
    },
    platformClass(platform) {
      if (platform === 'web') return 'badge-primary';
      if (platform === 'mobile') return 'badge-info';
      return 'badge-secondary';
    },
    stripMarkdown(content) {
      if (!content) return '';
      return content.replace(/[#*`\-_>\[\]()]/g, '').trim();
    },
    renderMarkdown(content) {
      if (!content) return '';
      // Simple markdown: headings, bold, lists, line breaks
      return content
          .replace(/### (.+)/g, '<h5>$1</h5>')
          .replace(/## (.+)/g, '<h4>$1</h4>')
          .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
          .replace(/^- (.+)/gm, '<li>$1</li>')
          .replace(/(<li>.*<\/li>)/gs, '<ul>$1</ul>')
          .replace(/\n/g, '<br>');
    },
    formatTime(ts) {
      if (!ts) return '';
      return moment(ts).format('DD/MM/YYYY HH:mm');
    },
    preview(item) {
      this.previewItem = null;
      let loader = this.$loading.show();
      this.$service.get(this.service + '/' + item._id).then(response => {
        this.previewItem = response.data;
        this.showPreview = true;
      }).finally(() => {
        loader.hide();
      });
    },
    showPublishModal(item) {
      this.publishItem = item;
      this.publishOptions = {notify: false, notifyTarget: 'all'};
      this.showPublish = true;
    },
    doPublish(evt) {
      evt.preventDefault();
      if (!this.publishItem) return;
      let loader = this.$loading.show();
      this.$service.put(this.service + '/' + this.publishItem._id + '/publish', {
        notify: this.publishOptions.notify,
        notifyTarget: this.publishOptions.notifyTarget,
      }).then(() => {
        this.showPublish = false;
        this.load();
        this.$swal({title: 'Published!', icon: 'success'});
      }).catch(error => {
        if (error.response && error.response.data && error.response.data.msg) {
          this.$swal({text: error.response.data.msg, icon: 'error'});
        }
      }).finally(() => {
        loader.hide();
      });
    },
    unpublish(id) {
      this.$swal({
        title: 'Gỡ publish?',
        text: 'Release note sẽ trở về trạng thái draft',
        icon: 'warning',
        showCancelButton: true,
      }).then(result => {
        if (result.value) {
          let loader = this.$loading.show();
          this.$service.put(this.service + '/' + id + '/unpublish').then(() => {
            this.load();
          }).finally(() => {
            loader.hide();
          });
        }
      });
    },
  },
}
</script>
